/**
 * SessionReviewPage — post-session feedback flow.
 *
 * Route: /session/:bookingId/review
 * Auth:  ProtectedRoute — buyer must be signed in as the booking's user.
 *
 * The missing journey piece flagged in the 2026-05-28 UX audit: live
 * sessions used to end in silence (no rating capture, no flywheel signal).
 * Async reviews already had this via `VerificationDetailPage`; this page
 * brings live sessions to parity.
 *
 * State machine (see `lib/sessionReview.js::REVIEW_STATES`):
 *   loading → idle → submitting → success
 *                 ↓
 *           already_submitted
 *           too_early
 *           wrong_account
 *           session_expired (auto-retry on relogin)
 *           unauthenticated
 *           not_found
 *           error_other
 *
 * Discipline (all from the BR-checkout-ack pattern):
 *   • api client (refresh-on-401 + ApiError wrap)
 *   • no `error.response.data` rendered raw
 *   • feature-flagged behind `session_review_v1`
 *   • telemetry events documented in `lib/telemetry.js` JSDoc
 *   • source-grep regression that "Not authenticated" never reaches DOM
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Clock, AlertTriangle, MessageSquare } from "lucide-react";
import api from "@/api";
import { useAuth } from "@/App";
import { ApiError } from "@/lib/apiError";
import { track } from "@/lib/telemetry";
import { isFlagOn } from "@/lib/flags";
import {
  REVIEW_STATES,
  classifyReviewError,
  subPromptFor,
} from "@/lib/sessionReview";
import StarPicker from "@/components/StarPicker";
import MarketingNav from "@/components/MarketingNav";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

function trackIfEnabled(name, props) {
  if (!isFlagOn("session_review_v1")) return;
  track(name, props);
}

export default function SessionReviewPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user, status: authStatus } = useAuth();

  const [state, setState]       = useState(REVIEW_STATES.LOADING);
  const [booking, setBooking]   = useState(null);
  const [existing, setExisting] = useState(null);    // pre-existing review row
  const [rating, setRating]     = useState(null);
  const [comment, setComment]   = useState("");
  const [submitError, setSubmitError] = useState(null);

  // Initial load: check whether this booking already has a review (we want
  // to render the already_submitted state immediately rather than letting
  // the user fill out a duplicate form). Also pulls booking metadata so we
  // can render expert_name + scheduled_at in the header.
  useEffect(() => {
    if (authStatus === "loading") return;
    if (authStatus === "unauthenticated") {
      setState(REVIEW_STATES.UNAUTHENTICATED);
      return;
    }

    let cancelled = false;
    async function load() {
      try {
        // First check for existing review — if found, we're in already_submitted
        try {
          const res = await api.get(`/bookings/${bookingId}/review`);
          if (cancelled) return;
          setExisting(res.data);
          setBooking({
            booking_id:   res.data.booking_id,
            expert_id:    res.data.expert_id,
            expert_name:  res.data.expert_name,
          });
          setState(REVIEW_STATES.ALREADY_SUBMITTED);
          trackIfEnabled("session_review_prompt_viewed", {
            booking_id: bookingId,
            hours_since_session: null,  // unknown for already-submitted path
          });
          return;
        } catch (err) {
          const e = ApiError.from(err);
          if (e.status !== 404) {
            // Real error (not "no review yet"). Route via the classifier.
            setSubmitError(e);
            setState(classifyReviewError(e));
            return;
          }
          // 404 means "no review yet" — fall through to the awaiting list to
          // confirm the booking exists + is past-time + paid.
        }

        // Look up the booking in awaiting-review list. If it's there, we
        // can render the form. Otherwise we know something's off
        // (booking doesn't exist, isn't paid, or session hasn't ended).
        const listRes = await api.get(`/bookings/me/awaiting-review`);
        if (cancelled) return;
        const found = (listRes.data?.bookings || []).find(
          (b) => b.booking_id === bookingId
        );
        if (found) {
          setBooking(found);
          setState(REVIEW_STATES.IDLE);
          // hours_since_session is computed for the prompt-viewed event so
          // we can later analyse "do users review within an hour vs days?"
          const sched = new Date(`${found.scheduled_date}T${found.scheduled_time}:00Z`);
          const hoursSince = Math.max(0, (Date.now() - sched.getTime()) / 3_600_000);
          trackIfEnabled("session_review_prompt_viewed", {
            booking_id: bookingId,
            hours_since_session: Math.round(hoursSince * 10) / 10,
          });
          return;
        }

        // Not in awaiting list — could be future, not paid, or not theirs.
        // The most common cases: session_in_future, not_found.
        // We try a direct booking-info fetch to distinguish.
        try {
          await api.get(`/bookings/${bookingId}`);
          // Booking exists but isn't in awaiting-review → session hasn't ended.
          setState(REVIEW_STATES.TOO_EARLY);
        } catch (innerErr) {
          const e2 = ApiError.from(innerErr);
          setSubmitError(e2);
          setState(classifyReviewError(e2));
        }
      } catch (err) {
        if (cancelled) return;
        const e = ApiError.from(err);
        setSubmitError(e);
        setState(classifyReviewError(e));
      }
    }
    load();
    return () => { cancelled = true; };
  }, [bookingId, authStatus]);

  async function handleSubmit() {
    if (!rating) return;
    setState(REVIEW_STATES.SUBMITTING);
    setSubmitError(null);
    try {
      const res = await api.post("/reviews", {
        booking_id: bookingId,
        rating,
        comment: comment.trim() || null,
      });
      setExisting(res.data);
      setState(REVIEW_STATES.SUCCESS);
      trackIfEnabled("session_review_submitted", {
        booking_id:  bookingId,
        rating,
        has_comment: comment.trim().length > 0,
      });
    } catch (err) {
      const e = ApiError.from(err);
      setSubmitError(e);
      setState(classifyReviewError(e));
      trackIfEnabled("session_review_submit_failed", {
        booking_id: bookingId,
        status:     e.status ?? null,
        kind:       e.kind || "unknown",
      });
    }
  }

  function handleSkip() {
    trackIfEnabled("session_review_skipped", {
      booking_id:     bookingId,
      from_dashboard: false,
    });
    navigate("/dashboard");
  }

  function handleRatingChange(n) {
    setRating(n);
    trackIfEnabled("session_review_rating_selected", {
      booking_id: bookingId,
      rating:     n,
    });
  }

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-canvas text-ink font-body">
      <MarketingNav />
      <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
        {state === REVIEW_STATES.LOADING && <LoadingState />}
        {state === REVIEW_STATES.UNAUTHENTICATED && (
          <UnauthState bookingId={bookingId} />
        )}
        {state === REVIEW_STATES.SESSION_EXPIRED && (
          <UnauthState bookingId={bookingId} variant="expired" />
        )}
        {state === REVIEW_STATES.NOT_FOUND && <NotFoundState />}
        {state === REVIEW_STATES.WRONG_ACCOUNT && <WrongAccountState />}
        {state === REVIEW_STATES.TOO_EARLY && (
          <TooEarlyState booking={booking} />
        )}
        {state === REVIEW_STATES.ALREADY_SUBMITTED && (
          <AlreadySubmittedState review={existing} navigate={navigate} />
        )}
        {state === REVIEW_STATES.SUCCESS && (
          <SuccessState review={existing} navigate={navigate} />
        )}
        {(state === REVIEW_STATES.IDLE ||
          state === REVIEW_STATES.SUBMITTING ||
          state === REVIEW_STATES.ERROR_OTHER) && (
          <FormState
            booking={booking}
            rating={rating}
            comment={comment}
            onRatingChange={handleRatingChange}
            onCommentChange={setComment}
            onSubmit={handleSubmit}
            onSkip={handleSkip}
            submitting={state === REVIEW_STATES.SUBMITTING}
            submitError={state === REVIEW_STATES.ERROR_OTHER ? submitError : null}
          />
        )}
      </section>
    </div>
  );
}

// ─── State sub-components ────────────────────────────────────────────────

function LoadingState() {
  return (
    <div className="text-center py-16" aria-busy="true" aria-label="Loading review">
      <div className="w-8 h-8 border-2 border-brand-green border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-slateline-500">Loading…</p>
    </div>
  );
}

function UnauthState({ bookingId, variant }) {
  const returnTo = encodeURIComponent(`/session/${bookingId}/review`);
  const heading = variant === "expired"
    ? "Sign in again to leave your review"
    : "Sign in to leave your review";
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <h1 className="font-display text-3xl text-ink">{heading}</h1>
      <p className="mt-3 text-slateline-600">
        You need to be signed in to review this session.
      </p>
      <Link
        to={`/login?return_to=/session/${bookingId}/review`}
        className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-brand-green hover:bg-brand-greenHover text-white text-sm font-semibold transition-colors"
        data-return-to={returnTo}
      >
        Sign in →
      </Link>
    </motion.div>
  );
}

function NotFoundState() {
  return (
    <div className="text-center py-16">
      <AlertTriangle className="w-10 h-10 text-slateline-400 mx-auto mb-3" aria-hidden="true" />
      <h1 className="font-display text-3xl text-ink">Booking not found</h1>
      <p className="mt-3 text-slateline-500 max-w-md mx-auto">
        We couldn't find this session — the link may be wrong, or the
        booking may belong to a different account.
      </p>
      <Link
        to="/dashboard"
        className="mt-6 inline-flex items-center h-10 px-4 rounded-md border border-slateline-300 hover:border-ink text-ink text-sm font-medium"
      >
        Back to dashboard
      </Link>
    </div>
  );
}

function WrongAccountState() {
  return (
    <div className="text-center py-16">
      <AlertTriangle className="w-10 h-10 text-slateline-400 mx-auto mb-3" aria-hidden="true" />
      <h1 className="font-display text-3xl text-ink">This isn't your session</h1>
      <p className="mt-3 text-slateline-500 max-w-md mx-auto">
        Only the buyer can leave a review. If you booked this session
        under a different account, sign in with that one.
      </p>
      <div className="mt-6 flex items-center justify-center gap-3">
        <Link
          to="/login"
          className="inline-flex items-center h-10 px-4 rounded-md bg-brand-green hover:bg-brand-greenHover text-white text-sm font-semibold"
        >
          Switch account
        </Link>
        <Link
          to="/dashboard"
          className="inline-flex items-center h-10 px-4 rounded-md border border-slateline-300 hover:border-ink text-ink text-sm font-medium"
        >
          Dashboard
        </Link>
      </div>
    </div>
  );
}

function TooEarlyState({ booking }) {
  return (
    <div className="text-center py-16">
      <Clock className="w-10 h-10 text-brand-green mx-auto mb-3" aria-hidden="true" />
      <h1 className="font-display text-3xl text-ink">
        You can review after your session
      </h1>
      <p className="mt-3 text-slateline-500 max-w-md mx-auto">
        This session hasn't ended yet
        {booking?.expert_name && <> with <span className="text-ink font-semibold">{booking.expert_name}</span></>}
        . Come back once the session is complete and we'll capture your feedback.
      </p>
      <Link
        to="/dashboard"
        className="mt-6 inline-flex items-center h-10 px-4 rounded-md border border-slateline-300 hover:border-ink text-ink text-sm font-medium"
      >
        Back to dashboard
      </Link>
    </div>
  );
}

function AlreadySubmittedState({ review, navigate }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center py-12">
      <CheckCircle2 className="w-10 h-10 text-brand-green mx-auto mb-3" aria-hidden="true" />
      <h1 className="font-display text-3xl text-ink">You already reviewed this</h1>
      <p className="mt-3 text-slateline-500">
        Thanks for the feedback{review?.expert_name && <> on your session with <span className="text-ink font-semibold">{review.expert_name}</span></>}.
      </p>
      <div className="mt-6 inline-block bg-white border border-slateline-200 rounded-xl p-5 text-left">
        <div className="flex items-center gap-1">
          <StarPicker value={review?.rating} onChange={() => {}} disabled />
        </div>
        {review?.comment && (
          <p className="mt-3 text-sm text-slateline-600 max-w-md">"{review.comment}"</p>
        )}
      </div>
      <div className="mt-8">
        <Button
          onClick={() => navigate("/dashboard")}
          className="bg-brand-green hover:bg-brand-greenHover text-white font-semibold px-6 py-3 rounded-md text-sm"
        >
          Return to dashboard
        </Button>
      </div>
    </motion.div>
  );
}

function SuccessState({ review, navigate }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center py-12">
      <CheckCircle2 className="w-12 h-12 text-brand-green mx-auto mb-4" aria-hidden="true" />
      <h1 className="font-display text-3xl text-ink">Thanks for the review</h1>
      <p className="mt-3 text-slateline-500 max-w-md mx-auto">
        Your feedback helps other founders find the right expert — and it
        helps the expert know what's working.
      </p>
      <div className="mt-6 inline-block bg-white border border-slateline-200 rounded-xl p-5 text-left">
        <div className="flex items-center gap-1">
          <StarPicker value={review?.rating} onChange={() => {}} disabled />
        </div>
        {review?.comment && (
          <p className="mt-3 text-sm text-slateline-600 max-w-md">"{review.comment}"</p>
        )}
      </div>
      <div className="mt-8 flex items-center justify-center gap-3">
        <Button
          onClick={() => navigate("/dashboard")}
          className="bg-brand-green hover:bg-brand-greenHover text-white font-semibold px-6 py-3 rounded-md text-sm"
        >
          Back to dashboard
        </Button>
        <Link
          to="/experts"
          className="inline-flex items-center h-10 px-4 rounded-md border border-slateline-300 hover:border-ink text-ink text-sm font-medium"
        >
          Browse more experts
        </Link>
      </div>
    </motion.div>
  );
}

function FormState({
  booking, rating, comment,
  onRatingChange, onCommentChange,
  onSubmit, onSkip,
  submitting, submitError,
}) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green">
          Session review
        </p>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl text-ink leading-tight">
          How was your session
          {booking?.expert_name && <> with <span className="italic text-brand-green">{booking.expert_name}</span></>}?
        </h1>
        {booking?.scheduled_at && (
          <p className="mt-3 text-sm text-slateline-500">
            {booking.scheduled_at}
            {booking.duration_min && <> · {booking.duration_min} minutes</>}
          </p>
        )}
      </div>

      <div className="bg-white border border-slateline-200 rounded-xl p-6 sm:p-8">
        <div>
          <label className="text-xs text-slateline-500 tracking-wider uppercase font-semibold">
            Your rating
          </label>
          <div className="mt-3">
            <StarPicker value={rating} onChange={onRatingChange} disabled={submitting} />
          </div>
        </div>

        <div className="mt-6">
          <label className="text-xs text-slateline-500 tracking-wider uppercase font-semibold" htmlFor="review-comment">
            {subPromptFor(rating)}
          </label>
          <Textarea
            id="review-comment"
            value={comment}
            onChange={(e) => onCommentChange(e.target.value)}
            disabled={submitting}
            placeholder="Share specifics — they help the expert improve and help other founders pick wisely."
            className="mt-2 min-h-[120px] bg-canvas border-slateline-200 text-sm"
          />
        </div>

        {submitError && (
          <p className="mt-4 text-sm text-red-600" role="alert">
            {submitError.message}
          </p>
        )}

        <div className="mt-6 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onSkip}
            className="text-sm text-slateline-500 hover:text-ink"
          >
            Skip for now
          </button>
          <Button
            onClick={onSubmit}
            disabled={!rating || submitting}
            className="bg-brand-green hover:bg-brand-greenHover disabled:opacity-50 text-white font-semibold px-6 py-3 rounded-md text-sm"
          >
            {submitting ? "Submitting…" : "Submit review"}
          </Button>
        </div>
        {!rating && (
          <p className="mt-3 text-xs text-slateline-500 text-right">
            Pick a star to enable submit.
          </p>
        )}
      </div>

      <div className="mt-8 text-center text-xs text-slateline-400">
        <MessageSquare className="w-4 h-4 inline-block mr-1" aria-hidden="true" />
        Your review is shared with the expert and shown on their public profile.
      </div>
    </motion.div>
  );
}
